import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import SongList from "components/SongList/SongList";
import { rootSelectors, RootState } from "state/rootReducer";
import { actions } from "state/slices/songsSlice";
import {
  actions as userActions,
  SongFeedbackAndSeed,
} from "state/slices/userSlice";
import SeedsModal from "pages/FreePlayPage/components/SeedsModal";
import ExportPlaylistModal, {
  ExportPlaylistParams,
} from "pages/FreePlayPage/components/ExportPlaylistModal";
import { useOpenSong } from "shared/navigation";
import {
  CollapseIcon,
  DropdownIcon,
  ExportIcon,
  ICON_STROKE,
  RefreshIcon,
  SeedIcon,
  SettingsIcon,
  ShuffleIcon,
  TopIcon,
} from "shared/icons";

const DEFAULT_ITEMS = 50;
/**
 * Candidate pool size, sent to the API as `depth`. Shown as "Variety" in
 * Shuffle mode; Top mode hides the slider and always sends this default, so a
 * value the user can no longer see never shapes their results.
 */
const DEFAULT_DEPTH = 500;

type PickMode = "top" | "shuffle";

/**
 * Recommender parameters that used to have their own controls in this toolbar.
 *
 * They are still sent to the API on every request, so the backend contract is
 * unchanged — they simply always take the value the UI defaulted to. They live
 * here, rather than inlined at the call site, so an "Advanced" panel can expose
 * them again by turning these back into component state and nothing else.
 */
const ADVANCED_DEFAULTS = {
  /** Popularity penalty. Was the "Obsc" slider, range 0.01-0.99. */
  popPenalty: 0.5,
  /** Whether seed tracks can appear in their own results. Was a checkbox. */
  includeSeeds: false,
  /** Whether already-liked tracks can appear in results. Was a checkbox. */
  includeLikes: false,
} as const;

interface OwnProps {
  includeScore: boolean;
}

interface StateProps {
  activeSeeds: SongFeedbackAndSeed[];
  recommendations: SongRecommendation[] | null;
  songFeedback: SongFeedback[] | null;
}

interface DispatchProps {
  onFetchSongs: typeof actions.fetchRecommendationsFromLikes;
  onClearRecs: typeof actions.clearRecommendationsFromLikes;
  onAddToPlaylist: typeof actions.addToPlaylist;
  onFetchSongFeedback: typeof userActions.fetchSongFeedback;
  onUpdateSeedStatus: typeof userActions.updateSeedStatus;
  onExportPlaylist: typeof userActions.createUserPlaylist;
}

type FreePlayPageProps = OwnProps & StateProps & ResolveThunks<DispatchProps>;

export function FreePlayPage(props: FreePlayPageProps): JSX.Element {
  const openSong = useOpenSong();

  const [fetching, setFetching] = useState(false);
  const [inSeedsModal, setInSeedsModal] = useState(false);
  const [inExportPlaylistModal, setInExportPlaylistModal] = useState(false);
  const [mode, setMode] = useState<PickMode>("top");
  const [depth, setDepth] = useState(DEFAULT_DEPTH);
  const [count, setCount] = useState(DEFAULT_ITEMS);
  const [algo] = useState<"v1" | "v2">("v2");
  /** Phones only: whether the full settings are open while results show. */
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasResults = Number(props.recommendations?.length) > 0;
  // On phones the settings fold into a single row once there are results, or
  // some are on the way, so the list gets the screen. From sm they always show.
  const settingsCollapsed = (hasResults || fetching) && !settingsOpen;

  /** Top picks the highest-scoring results; Shuffle draws a weighted sample. */
  function fetchRecs(): void {
    const top = mode === "top";
    setFetching(true);
    setSettingsOpen(false);

    props.onClearRecs();
    props.onFetchSongs(
      count,
      top ? DEFAULT_DEPTH : depth,
      top,
      ADVANCED_DEFAULTS.includeSeeds,
      ADVANCED_DEFAULTS.includeLikes,
      ADVANCED_DEFAULTS.popPenalty,
      algo
    );
  }

  useEffect(() => {
    if (props.recommendations !== null) {
      setFetching(false);
    }
  }, [props.recommendations]);

  function openModalAndLoadSeeds(): void {
    if (props.songFeedback === null) {
      props.onFetchSongFeedback();
    }

    setInSeedsModal(true);
  }

  function closeModal(): void {
    setInSeedsModal(false);
  }

  function saveModalSeeds(songs: Song[]): void {
    props.onUpdateSeedStatus(songs);

    closeModal();
  }

  function selectSong(song: Song): void {
    openSong(song);
  }

  /**
   * Phones, once there are results: the settings as one row. A summary that
   * opens the full settings, then Get recommendations and Export as icons.
   * The full card is about 278px tall on a phone; this is about 54px.
   */
  function renderCompactSettings(): JSX.Element {
    const modeLabel = mode === "top" ? "Top" : "Shuffle";

    return (
      <div
        role="toolbar"
        aria-label="Listen settings"
        className="sm:hidden flex items-center gap-2 mb-3 p-2 rounded-token border border-rule bg-surface"
      >
        <button
          type="button"
          className="flex-1 min-w-0 flex items-center gap-2 h-9 px-2 rounded text-sm text-ink-2 hover:bg-row-hover"
          onClick={() => setSettingsOpen(true)}
          aria-expanded={false}
          title="Adjust settings"
        >
          <SettingsIcon
            size={16}
            strokeWidth={ICON_STROKE}
            className="flex-none"
            aria-hidden="true"
          />
          <span className="truncate">
            <span className="sr-only">Adjust settings: </span>
            <span className="font-medium text-ink tabular-nums">
              {props.activeSeeds.length}
            </span>{" "}
            seeds ·{" "}
            <span className="font-medium text-ink tabular-nums">{count}</span>{" "}
            results · {modeLabel}
          </span>
          <DropdownIcon
            size={16}
            strokeWidth={ICON_STROKE}
            className="flex-none ml-auto text-muted"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className="button-primary flex-none flex items-center justify-center w-9 h-9 p-0"
          onClick={() => fetchRecs()}
          aria-label="Get recommendations"
          title="Get recommendations"
        >
          <RefreshIcon size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
        </button>
        {hasResults && (
          <button
            type="button"
            className="button-spotify flex-none flex items-center justify-center w-9 h-9 p-0"
            onClick={() => setInExportPlaylistModal(true)}
            aria-label="Export to Spotify"
            title="Export to Spotify"
          >
            <ExportIcon size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  async function exportPlaylist(params: ExportPlaylistParams): Promise<void> {
    await props.onExportPlaylist({
      ...params,
      trackIds: (props.recommendations ?? [])?.map((rs) => rs.song.spotify_id),
    });
    setInExportPlaylistModal(false);
  }

  function render(): JSX.Element {
    return (
      <div className="p-2 md:p-4 w-full flex flex-col">
        {settingsCollapsed && renderCompactSettings()}
        {/* One control bar: what to draw from, how much to return, and how to
            pick it. The recommender's finer knobs live in ADVANCED_DEFAULTS. */}
        <div
          className={`relative rounded-token border border-rule bg-surface p-3 md:p-4 mb-4 w-full max-w-3xl mx-auto ${
            settingsCollapsed ? "hidden sm:block" : ""
          }`}
        >
          {hasResults && settingsOpen && (
            <button
              type="button"
              className="sm:hidden button-small absolute top-2 right-2 flex items-center justify-center w-9 h-9"
              onClick={() => setSettingsOpen(false)}
              aria-label="Hide settings"
              title="Hide settings"
            >
              <CollapseIcon
                size={18}
                strokeWidth={ICON_STROKE}
                aria-hidden="true"
              />
            </button>
          )}
          <div className="flex flex-col lg:flex-row lg:items-center justify-center gap-4">
            <button
              className="button-generic flex items-center gap-2 self-start lg:self-auto shrink-0"
              onClick={() => openModalAndLoadSeeds()}
            >
              <SeedIcon
                size={17}
                strokeWidth={ICON_STROKE}
                aria-hidden="true"
              />
              Seeds
              <span className="ml-1 rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink tabular-nums">
                {props.activeSeeds.length}
              </span>
            </button>

            <div
              className="hidden lg:block w-px self-stretch bg-rule"
              aria-hidden="true"
            ></div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <label className="flex items-center gap-3">
                <span className="text-sm text-ink-2 w-24 shrink-0">
                  Results
                  <span className="ml-2 font-semibold text-ink tabular-nums">
                    {count}
                  </span>
                </span>
                <input
                  className="w-32 accent-accent"
                  onChange={(e) => setCount(Number(e.target.value))}
                  type="range"
                  min="10"
                  max="150"
                  value={count}
                  aria-label="Number of results"
                />
              </label>

              {/* Only Shuffle samples from the pool, so only Shuffle offers
                  control over its size. Hidden (not disabled) in Top mode. */}
              {mode === "shuffle" && (
                <label className="flex items-center gap-3">
                  <span className="text-sm text-ink-2 w-24 shrink-0">
                    Variety
                    <span className="ml-2 font-semibold text-ink tabular-nums">
                      {depth}
                    </span>
                  </span>
                  <input
                    className="w-32 accent-accent"
                    onChange={(e) => setDepth(Number(e.target.value))}
                    type="range"
                    min="150"
                    max="1500"
                    value={depth}
                    aria-label="Variety"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-4 border-t border-rule">
            {/* A segmented toggle: choosing a mode doesn't fetch on its own,
                so tweaking settings never discards the current list. */}
            <div
              role="group"
              aria-label="How to pick results"
              className="inline-flex gap-0.5 p-0.5 rounded bg-raised border border-rule-strong"
            >
              {(
                [
                  { value: "top", label: "Top", Icon: TopIcon },
                  { value: "shuffle", label: "Shuffle", Icon: ShuffleIcon },
                ] as const
              ).map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={mode === value}
                  // Tighter on phones so "Get recommendations" fits beside it.
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-sm ${
                    mode === value ? "active-tab" : "inactive-tab"
                  }`}
                  onClick={() => setMode(value)}
                >
                  <Icon
                    size={16}
                    strokeWidth={ICON_STROKE}
                    aria-hidden="true"
                  />
                  {label}
                </button>
              ))}
            </div>

            {/* Smaller on phones, to share a line with the Top/Shuffle toggle
                (and match its height) rather than wrapping beneath it. */}
            <button
              type="button"
              className="button-primary text-sm sm:text-base px-3 sm:px-[0.95rem]"
              onClick={() => fetchRecs()}
            >
              Get recommendations
            </button>

            {Number(props.recommendations?.length) > 0 ? (
              <button
                className="button-spotify flex items-center gap-2"
                onClick={() => setInExportPlaylistModal(true)}
              >
                <ExportIcon
                  size={17}
                  strokeWidth={ICON_STROKE}
                  aria-hidden="true"
                />
                Export to Spotify
              </button>
            ) : null}
          </div>
        </div>
        {fetching && <LoadingSpinner border={false} />}
        {props.recommendations && (
          <SongList
            maxSongs={props.recommendations.length}
            songs={props.recommendations.map((sr) => sr.song)}
            otherdata={
              props.includeScore
                ? props.recommendations.map((sr) => sr.score + "")
                : undefined
            }
            otherdataName={props.includeScore ? "Score" : undefined}
            selectSongHandler={(s) => selectSong(s)}
          />
        )}
        {inSeedsModal && props.songFeedback && (
          <SeedsModal
            onCancel={() => closeModal()}
            onSaveSeeds={(songs) => saveModalSeeds(songs)}
          />
        )}
        {inExportPlaylistModal && props.recommendations?.length && (
          <ExportPlaylistModal
            descriptionHint={`Created from ${
              props.activeSeeds[0].song.name
            } and ${props.activeSeeds.length - 1} more seeds.`}
            songCount={props.recommendations.length}
            onCancel={() => setInExportPlaylistModal(false)}
            onExport={(params) => exportPlaylist(params)}
          />
        )}
      </div>
    );
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    activeSeeds: rootSelectors.user.getActiveSeeds(state),
    recommendations: state.songs.songRecsFromLikes,
    songFeedback: state.user.songFeedbackByGid
      ? Object.entries(state.user.songFeedbackByGid).map(([_, sf]) => sf)
      : state.user.songFeedbackByGid,
  };
}

const mapDispatch: DispatchProps = {
  onFetchSongs: actions.fetchRecommendationsFromLikes,
  onClearRecs: actions.clearRecommendationsFromLikes,
  onAddToPlaylist: actions.addToPlaylist,
  onFetchSongFeedback: userActions.fetchSongFeedback,
  onUpdateSeedStatus: userActions.updateSeedStatus,
  onExportPlaylist: userActions.createUserPlaylist,
};

export default connect(mapState, mapDispatch)(FreePlayPage);
